import type { WavesChart } from '../../wavesChart.js';
import type { WavesReplayInput } from '../../replay.js';
import { step, type WavesEngineInput } from './core.js';
import type { WavesEngineConfig, WavesEngineState } from './state.js';
import { createInitialWavesState } from './state.js';

export function makePlannerState(
  chart: WavesChart,
  beat: number,
  y: number,
  pressing: boolean,
): WavesEngineState {
  const state = createInitialWavesState(chart);
  state.beat = beat;
  state.lastEdgeBeat = beat;
  state.lastEdgeY = y;
  state.playerY = y;
  state.pressing = pressing;
  return state;
}

export function planSegmentInputs(
  config: WavesEngineConfig,
  chart: WavesChart,
  fromBeat: number,
  toBeat: number,
  startY: number,
  startPressing: boolean,
  targetY: number,
): { inputs: WavesReplayInput[]; endPressing: boolean } {
  const dt = toBeat - fromBeat;
  if (dt <= 1e-9) {
    return { inputs: [], endPressing: startPressing };
  }

  const verify = (
    inputs: WavesEngineInput[],
  ): { y: number; pressing: boolean } => {
    const state = makePlannerState(chart, fromBeat, startY, startPressing);
    const out = step({ config, state, fromBeat, toBeat, inputs });
    return { y: out.state.playerY, pressing: out.state.pressing };
  };

  const noFlip = verify([]);
  if (Math.abs(noFlip.y - targetY) < 1e-2) {
    return { inputs: [], endPressing: noFlip.pressing };
  }
  const flipAtStart: WavesEngineInput[] = [
    { beat: fromBeat, type: startPressing ? 'up' : 'down' },
  ];
  const atStart = verify(flipAtStart);
  if (Math.abs(atStart.y - targetY) < 1e-2) {
    return {
      inputs: [{ beat: fromBeat, type: startPressing ? 'up' : 'down' }],
      endPressing: atStart.pressing,
    };
  }

  let lo = fromBeat;
  let hi = toBeat;
  let bestInputs: WavesReplayInput[] = [];
  let bestEndPressing = startPressing;
  let bestErr = Infinity;
  for (let iter = 0; iter < 48; iter += 1) {
    const flipBeat = (lo + hi) / 2;
    const trial: WavesEngineInput[] = [
      { beat: flipBeat, type: startPressing ? 'up' : 'down' },
    ];
    const end = verify(trial);
    const err = Math.abs(end.y - targetY);
    if (err < bestErr) {
      bestErr = err;
      bestInputs = [{ beat: flipBeat, type: startPressing ? 'up' : 'down' }];
      bestEndPressing = end.pressing;
    }
    if (end.y < targetY) {
      if (startPressing) {
        hi = flipBeat;
      } else {
        lo = flipBeat;
      }
    } else if (end.y > targetY) {
      if (startPressing) {
        lo = flipBeat;
      } else {
        hi = flipBeat;
      }
    } else {
      bestInputs = trial;
      bestEndPressing = end.pressing;
      break;
    }
  }
  if (bestErr > 1e-1) {
    throw new Error(
      `Emit phase: could not reach Y=${targetY.toFixed(2)} at beat ${toBeat} from Y=${startY.toFixed(2)} (bestErr=${bestErr.toFixed(3)})`,
    );
  }
  return { inputs: bestInputs, endPressing: bestEndPressing };
}
